import { Component, OnInit, DestroyRef, ChangeDetectorRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import { ProfileService } from './profile.service';
import { AuthService, Organization } from '../auth/auth.service';
import { InstructorOrganizationService } from '../instructor/instructor-organization.service';
import { IconComponent } from '../shared/components/icon/icon.component';
import { ThemeSwitcherComponent } from '../shared/components/theme-switcher.component';
import { EmailNotificationSettingsComponent } from './email-notification-settings/email-notification-settings.component';
import { ProfileFieldsComponent } from './profile-fields/profile-fields.component';
import { ProfileGateBannerComponent } from './profile-fields/profile-gate-banner.component';
import { sanitizeCourseReturnUrl } from '../shared/guards/profile-completion.utils';

@Component({
  selector: 'app-profile',
  standalone: true,
  imports: [CommonModule, RouterModule, IconComponent, ThemeSwitcherComponent, EmailNotificationSettingsComponent, ProfileFieldsComponent, ProfileGateBannerComponent],
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss']
})
export class ProfileComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);

  user: any = null;
  organizations: any[] = [];
  isLoading = true;
  errorMessage: string | null = null;
  successMessage: string | null = null;
  settingDefaultOrgId: string | null = null;

  // Leave org state
  showLeaveModal = false;
  orgToLeave: any = null;
  leavingOrgId: string | null = null;
  leaveErrorMessage: string | null = null;

  /**
   * KAN-562 course gate. When profileCompletionGuard turns a learner away from
   * `/courses/:id` it sends them here as
   * `/profile?returnTo=/courses/:id&reason=complete-profile`. `returnTo`
   * round-trips through the URL bar, so it is sanitized down to a course
   * player path before anything links to it. Null on an ordinary visit, which
   * is what keeps the banner invisible for everyone else.
   */
  gateReturnUrl: string | null = null;

  /** Set by the My Information section once every requested field has a value. */
  requestedFieldsComplete = false;

  private route = inject(ActivatedRoute);

  constructor(
    private profileService: ProfileService,
    private authService: AuthService,
    private instructorOrgService: InstructorOrganizationService,
    private router: Router
  ) {}

  /**
   * Unit-completion emails are an instructor/admin feature. Keep the preference
   * available to people who instruct in any organization, but do not present an
   * inert instructor setting to learner-only accounts (KAN-553).
   */
  get canReceiveInstructorNotifications(): boolean {
    if (this.user?.isSystemAdmin) return true;
    return this.organizations.some(org => org?.role === 'instructor' || org?.role === 'admin');
  }

  /** KAN-562: the My Information section reports requested-field completion. */
  onRequestedCompleteChange(isComplete: boolean): void {
    this.requestedFieldsComplete = isComplete;
    // Zoneless: this arrives from the section's async load/save.
    this.cdr.markForCheck();
  }

  ngOnInit(): void {
    this.route.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        this.gateReturnUrl = sanitizeCourseReturnUrl(params['returnTo']);
        this.cdr.markForCheck();
      });

    this.loadUserProfile();

    // React to org changes (e.g., default changed from sidebar switcher)
    this.authService.userOrganizations$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((orgs): orgs is Organization[] => orgs !== null)
      )
      .subscribe((orgs) => {
        this.organizations = orgs;
        this.cdr.markForCheck();
      });
  }

  loadUserProfile(): void {
    this.isLoading = true;
    this.errorMessage = null;

    // Get current user from auth service (cached data)
    const currentUser = this.authService.getCurrentUser();

    if (currentUser) {
      this.user = currentUser;
      // Initially use cached organizations, then fetch fresh data
      this.organizations = currentUser.organizations || [];

      // Fetch fresh organization data from backend
      this.refreshOrganizations();
      this.refreshAccountDates(currentUser.id);
    } else {
      this.isLoading = false;
      this.errorMessage = 'Unable to load user profile';
    }
  }

  private refreshAccountDates(userId: string): void {
    this.profileService.getOwnProfile()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: profile => {
          // Preserve the independently refreshed organization context. This
          // response only supplies metadata omitted from the login payload.
          if (profile.id !== userId || this.user?.id !== userId) return;
          this.user = {
            ...this.user,
            createdAt: profile.createdAt,
            lastLoginAt: profile.lastLoginAt
          };
          this.cdr.markForCheck();
        },
        error: () => { /* Keep the rest of the profile available. */ }
      });
  }

  /**
   * Fetch fresh organization data from the backend
   * This ensures the profile shows up-to-date organization affiliations
   */
  private refreshOrganizations(): void {
    this.authService.getUserOrganizations()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          console.log('Fetched fresh user organizations:', response);

          // Handle API response - data may be wrapped in a 'data' property
          const data = response.data || response;

          // Update organizations with fresh data
          this.organizations = data.organizations || [];

          // Also update the cached user data
          if (this.user) {
            this.user.organizations = this.organizations;
            this.user.currentOrgId = data.currentOrgId;
            this.user.currentOrgRole = data.currentOrgRole;

            // Save updated user to cache
            this.profileService.setCurrentUser(this.user);
          }

          this.isLoading = false;
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error fetching organizations:', error);
          // Fall back to cached data (already set above)
          this.isLoading = false;
          this.cdr.markForCheck();
        }
      });
  }

  getOrganizationRole(org: any): string {
    const roleLabels: { [key: string]: string } = {
      'learner': 'Learner',
      'instructor': 'Instructor',
      'admin': 'Administrator'
    };
    return roleLabels[org.role] || org.role;
  }

  isCurrentOrganization(orgId: string): boolean {
    return this.user?.currentOrgId === orgId;
  }

  setDefaultOrganization(org: any): void {
    if (org.isDefault || this.settingDefaultOrgId) {
      return;
    }

    this.settingDefaultOrgId = org.id;
    this.successMessage = null;
    this.errorMessage = null;

    this.authService.updateDefaultOrganizationPreference(org.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.successMessage = `${org.name} is now your default organization. It will load automatically on your next login.`;
          this.settingDefaultOrgId = null;
          this.cdr.markForCheck();
          setTimeout(() => {
            this.successMessage = null;
            this.cdr.markForCheck();
          }, 4000);
        },
        error: (error) => {
          console.error('Failed to set default organization:', error);
          this.errorMessage = error.error?.message || 'Failed to set default organization. Please try again.';
          this.settingDefaultOrgId = null;
          this.cdr.markForCheck();
        }
      });
  }

  isSettingDefault(orgId: string): boolean {
    return this.settingDefaultOrgId === orgId;
  }

  openLeaveModal(org: any): void {
    this.orgToLeave = org;
    this.leaveErrorMessage = null;
    this.showLeaveModal = true;
  }

  cancelLeave(): void {
    this.showLeaveModal = false;
    this.orgToLeave = null;
    this.leaveErrorMessage = null;
  }

  confirmLeave(): void {
    if (!this.orgToLeave || this.leavingOrgId) return;

    const orgId = this.orgToLeave.id;
    const orgName = this.orgToLeave.name;
    const wasCurrentOrg = this.user?.currentOrgId === orgId;
    this.leavingOrgId = orgId;
    this.leaveErrorMessage = null;

    this.profileService.leaveOrganization(orgId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          const data = response.data || response;
          this.organizations = data.remainingOrganizations || [];

          // Update cached user
          if (this.user) {
            this.user.organizations = this.organizations;
            this.user.currentOrgId = data.newDefaultOrgId;
            this.user.currentOrgRole = data.newDefaultOrgRole;
            this.profileService.setCurrentUser(this.user);
          }

          // Update org services
          if (data.newDefaultOrgId) {
            this.instructorOrgService.setSelectedOrganization(data.newDefaultOrgId);
          } else {
            this.instructorOrgService.clearSelectedOrganization();
          }

          this.authService.notifyOrganizationsUpdated(this.organizations);

          this.showLeaveModal = false;
          this.orgToLeave = null;
          this.leavingOrgId = null;
          this.successMessage = `You have left ${orgName}.`;
          this.cdr.markForCheck();

          setTimeout(() => {
            this.successMessage = null;
            this.cdr.markForCheck();
          }, 4000);

          // Navigate to dashboard if we left the active org
          if (wasCurrentOrg && data.newDefaultOrgId) {
            this.router.navigate(['/dashboard'], { replaceUrl: true });
          }
        },
        error: (error: any) => {
          const errorData = error.error?.error || error.error;
          if (errorData?.code === 'LAST_ADMIN') {
            this.leaveErrorMessage = 'You are the last administrator. Transfer the admin role to another member before leaving.';
          } else {
            this.leaveErrorMessage = errorData?.message || 'Failed to leave organization. Please try again.';
          }
          this.leavingOrgId = null;
          this.cdr.markForCheck();
        }
      });
  }
}
